import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Volume2, VolumeX, Phone, Mic, MicOff, Loader2, MessageCircle, Sparkles, Navigation } from 'lucide-react';
import api from '../utils/api';

const TalkToPriya = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'hi-IN';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    }
    
    // Preload voices - more robust loading
    const loadVoices = () => {
      if (window.speechSynthesis) {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setAvailableVoices(voices);
          console.log('Voices loaded:', voices.length);
        }
      }
    };
    
    loadVoices();
    
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    const timer = setTimeout(loadVoices, 500);
    
    return () => {
      clearTimeout(timer);
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/voice-agent/settings');
      setSettings(res.data);
      
      if (res.data.welcome_message) {
        setMessages([{
          role: 'assistant',
          content: res.data.welcome_message
        }]);
        
        // Speak welcome message after voices are loaded
        setTimeout(() => {
          speak(res.data.welcome_message);
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to fetch voice agent settings:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const findBestVoice = (voices) => {
    if (!voices || voices.length === 0) return null;
    
    const voicePriorities = [
      (v) => v.name.toLowerCase().includes('google') && v.lang.includes('hi') && v.name.toLowerCase().includes('female'),
      (v) => v.lang.includes('hi') && v.name.toLowerCase().includes('female'),
      (v) => v.name.toLowerCase().includes('google') && v.lang.includes('hi'),
      (v) => v.lang.includes('hi') || v.lang === 'hi-IN',
      (v) => v.lang.includes('en-IN'),
      (v) => v.name.toLowerCase().includes('female') && v.lang.includes('en'),
      (v) => v.lang.includes('en'),
      () => true
    ];
    
    for (const check of voicePriorities) {
      const found = voices.find(check);
      if (found) return found;
    }
    
    return voices[0];
  };

  const speak = (text) => {
    if (!voiceEnabled || !window.speechSynthesis) {
      console.log('Speech disabled or not supported');
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = settings?.voice_lang || 'hi-IN';
    utterance.rate = settings?.voice_rate || 0.9;
    utterance.pitch = settings?.voice_pitch || 1.0;
    utterance.volume = 1.0;
    
    let voices = availableVoices.length > 0 ? availableVoices : window.speechSynthesis.getVoices();
    
    if (voices.length === 0) {
      console.log('No voices available, retrying...');
      setTimeout(() => {
        voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setAvailableVoices(voices);
          executeSpeak(utterance, voices);
        } else {
          executeSpeak(utterance, []);
        }
      }, 200);
      return;
    }
    
    executeSpeak(utterance, voices);
  };

  const executeSpeak = (utterance, voices) => {
    const selectedVoice = findBestVoice(voices);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
      console.log('Using voice:', selectedVoice.name, selectedVoice.lang);
    }
    
    utterance.onstart = () => {
      console.log('Speech started');
      setIsSpeaking(true);
    };
    utterance.onend = () => {
      console.log('Speech ended');
      setIsSpeaking(false);
    };
    utterance.onerror = (e) => {
      console.error('Speech error:', e.error);
      setIsSpeaking(false);
    };
    
    try {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.error('Failed to speak:', e);
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const startListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;
    
    const userMessage = inputText.trim();
    setInputText('');
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    setLoading(true);
    try {
      const res = await api.post('/voice-agent/chat', {
        message: userMessage,
        session_id: sessionId
      });
      
      const assistantMessage = res.data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      
      // Check if there's a navigation intent
      if (res.data.navigation?.should_navigate) {
        const { route, page_name } = res.data.navigation;
        setPendingNavigation({ route, page_name });
        
        // Auto-navigate after speaking completes (or after 2 seconds if voice disabled)
        const navDelay = voiceEnabled ? 3500 : 2000;
        setTimeout(() => {
          if (route) {
            navigate(route);
            setPendingNavigation(null);
          }
        }, navDelay);
      }
      
      if (voiceEnabled) {
        speak(assistantMessage);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'माफ़ करें, कुछ गड़बड़ हो गई। कृपया दोबारा कोशिश करें।' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateNow = () => {
    if (pendingNavigation?.route) {
      navigate(pendingNavigation.route);
      setPendingNavigation(null);
      stopSpeaking();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openWhatsApp = () => {
    const phone = settings?.whatsapp_number || '919579369830';
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  const quickQuestions = [
    "Products दिखाओ",
    "मेरे Orders दिखाओ",
    "Wallet पर ले जाओ",
    "Activation Pack क्या है?",
    "Referral से कैसे कमाएं?"
  ];

  const navigationExamples = [
    { text: "🛍️ Products", command: "Show me products" },
    { text: "📦 Orders", command: "Go to my orders" },
    { text: "💰 Wallet", command: "Open wallets" },
    { text: "🎯 Dashboard", command: "Take me to dashboard" },
    { text: "🔧 Tools", command: "Show free tools" },
  ];

  return (
    <div className="min-h-screen pt-20 pb-8 px-4" data-testid="talk-to-priya-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <img 
              src={settings?.agent_photo || '/priya-avatar.png'} 
              alt="Priya"
              className="w-32 h-32 rounded-full object-cover border-4 border-primary shadow-lg shadow-primary/30"
            />
            <span className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 rounded-full border-4 border-background animate-pulse"></span>
          </div>
          <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
            <Sparkles className="w-8 h-8 text-primary" />
            {settings?.agent_name || 'Priya'}
            <Sparkles className="w-8 h-8 text-primary" />
          </h1>
          <p className="text-muted-foreground text-lg">
            आपकी AI सहायक | Your AI Assistant
          </p>
          
          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                voiceEnabled ? 'bg-primary/20 text-primary' : 'bg-white/5 text-muted-foreground'
              }`}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              {voiceEnabled ? 'Voice On' : 'Voice Off'}
            </button>
            <button
              onClick={openWhatsApp}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-500 hover:bg-green-500/30"
            >
              <Phone className="w-4 h-4" />
              WhatsApp
            </button>
          </div>
        </div>

        {/* Chat Container */}
        <div className="glass-card rounded-2xl overflow-hidden">
          {/* Messages */}
          <div className="h-[400px] overflow-y-auto p-6 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-3`}>
                {msg.role === 'assistant' && (
                  <img 
                    src={settings?.agent_photo || '/priya-avatar.png'} 
                    alt="Priya"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div className={`max-w-[70%] p-4 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-primary-foreground rounded-br-sm' 
                    : 'bg-white/10 rounded-bl-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start gap-3">
                <img 
                  src={settings?.agent_photo || '/priya-avatar.png'} 
                  alt="Priya"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="bg-white/10 p-4 rounded-2xl rounded-bl-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-muted-foreground">Priya सोच रही है...</span>
                  </div>
                </div>
              </div>
            )}
            {isSpeaking && (
              <div className="flex justify-center">
                <button
                  onClick={stopSpeaking}
                  className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2"
                >
                  <Volume2 className="w-4 h-4 animate-pulse" />
                  बोल रही है... (रोकने के लिए क्लिक करें)
                </button>
              </div>
            )}
            {pendingNavigation && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={handleNavigateNow}
                  className="px-6 py-3 bg-green-500/20 text-green-400 rounded-full text-base flex items-center gap-3 hover:bg-green-500/30 transition-all animate-pulse hover:scale-105"
                  data-testid="navigate-now-btn"
                >
                  <Navigation className="w-5 h-5" />
                  अभी जाएं / Go Now →
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions & Navigation */}
          {messages.length <= 1 && (
            <div className="px-6 pb-4 space-y-4">
              {/* Voice Navigation Shortcuts */}
              <div>
                <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  Quick Navigation (बोलो या क्लिक करो):
                </p>
                <div className="flex flex-wrap gap-2">
                  {navigationExamples.map((nav, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputText(nav.command);
                        setTimeout(() => sendMessage(), 100);
                      }}
                      className="px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 rounded-full text-sm transition-colors"
                      data-testid={`nav-shortcut-${idx}`}
                    >
                      {nav.text}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Quick Questions */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputText(q);
                        setTimeout(() => sendMessage(), 100);
                      }}
                      className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-full text-sm transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-white/10 bg-white/5">
            <div className="flex items-center gap-3">
              <button
                onClick={isListening ? stopListening : startListening}
                className={`p-4 rounded-xl transition-all ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse scale-110' 
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input (Hindi)'}
              >
                {isListening ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="हिंदी या English में लिखें... Type or speak..."
                className="flex-1 bg-white/10 border border-white/10 rounded-xl px-5 py-4 text-lg"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim() || loading}
                className="p-4 bg-primary text-primary-foreground rounded-xl disabled:opacity-50 hover:scale-105 transition-transform"
              >
                <Send className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalkToPriya;
